/** Scale-8 decimal helpers for Travel finance (Budget + Expense). No Number/FX arithmetic. */

export const MONEY_SCALE = 8;
const MONEY_PATTERN = /^\d+(\.\d{1,8})?$/;

/**
 * Bounded ISO 4217 selector for Travel Budget UI.
 * Backend accepts any ^[A-Z]{3}$; repository has no formal Travel currency catalog.
 * Prefer Journey.baseCurrency when present; do not invent FX.
 */
export const BUDGET_CURRENCY_OPTIONS = Object.freeze([
  'TWD', 'USD', 'JPY', 'EUR', 'HKD', 'CNY', 'KRW', 'SGD', 'GBP', 'AUD', 'THB', 'MYR', 'VND',
]);

export function moneyToCanonicalString(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  if (!MONEY_PATTERN.test(raw)) return null;
  const [whole, fraction = ''] = raw.split('.');
  return `${whole}.${fraction.padEnd(MONEY_SCALE, '0').slice(0, MONEY_SCALE)}`;
}

export function formatMoneyDisplay(value) {
  const canonical = moneyToCanonicalString(value);
  if (!canonical) return String(value ?? '');
  return canonical.replace(/\.?0+$/, '') || '0';
}

function toScaledDigits(canonical) {
  const [whole, fraction = ''] = String(canonical).replace(/^-/, '').split('.');
  return `${whole}${fraction.padEnd(MONEY_SCALE, '0').slice(0, MONEY_SCALE)}`.replace(/^0+(?=\d)/, '') || '0';
}

function compareDigitStrings(left, right) {
  const a = left.replace(/^0+(?=\d)/, '') || '0';
  const b = right.replace(/^0+(?=\d)/, '') || '0';
  if (a.length !== b.length) return a.length > b.length ? 1 : -1;
  if (a === b) return 0;
  return a > b ? 1 : -1;
}

function addDigitStrings(left, right) {
  const a = left.padStart(Math.max(left.length, right.length), '0');
  const b = right.padStart(a.length, '0');
  let carry = 0;
  let out = '';
  for (let i = a.length - 1; i >= 0; i -= 1) {
    const sum = Number(a[i]) + Number(b[i]) + carry;
    out = String(sum % 10) + out;
    carry = Math.floor(sum / 10);
  }
  if (carry) out = String(carry) + out;
  return out.replace(/^0+(?=\d)/, '') || '0';
}

function subtractDigitStrings(left, right) {
  const cmp = compareDigitStrings(left, right);
  if (cmp === 0) return { negative: false, digits: '0' };
  const minuend = (cmp > 0 ? left : right).padStart(Math.max(left.length, right.length), '0');
  const subtrahend = (cmp > 0 ? right : left).padStart(minuend.length, '0');
  let borrow = 0;
  let out = '';
  for (let i = minuend.length - 1; i >= 0; i -= 1) {
    let digit = Number(minuend[i]) - Number(subtrahend[i]) - borrow;
    if (digit < 0) {
      digit += 10;
      borrow = 1;
    } else {
      borrow = 0;
    }
    out = String(digit) + out;
  }
  return { negative: cmp < 0, digits: out.replace(/^0+(?=\d)/, '') || '0' };
}

function fromScaledDigits(digits, negative = false) {
  const padded = digits.padStart(MONEY_SCALE + 1, '0');
  const whole = padded.slice(0, -MONEY_SCALE) || '0';
  const fraction = padded.slice(-MONEY_SCALE);
  const text = `${whole}.${fraction}`;
  return negative ? `-${text}` : text;
}

/** Scale-8 decimal string sum without Number floating point. */
export function sumMoneyStrings(values) {
  let digits = '0';
  for (const value of values) {
    const canonical = moneyToCanonicalString(value);
    if (!canonical) continue;
    digits = addDigitStrings(digits, toScaledDigits(canonical));
  }
  return fromScaledDigits(digits);
}

export function compareMoney(left, right) {
  const a = moneyToCanonicalString(left);
  const b = moneyToCanonicalString(right);
  if (!a || !b) return 0;
  return compareDigitStrings(toScaledDigits(a), toScaledDigits(b));
}

export function subtractMoneyStrings(left, right) {
  const a = moneyToCanonicalString(left) || '0.00000000';
  const b = moneyToCanonicalString(right) || '0.00000000';
  const result = subtractDigitStrings(toScaledDigits(a), toScaledDigits(b));
  return fromScaledDigits(result.digits, result.negative);
}

/**
 * Progress percentage for display only (0–100, capped).
 * Uses scale-8 digit math — not monetary authority / FX.
 */
export function moneyUtilizationPercent(spent, total) {
  const totalCanonical = moneyToCanonicalString(total);
  if (!totalCanonical || compareMoney(totalCanonical, '0') <= 0) return 0;
  const spentCanonical = moneyToCanonicalString(spent) || '0.00000000';
  if (compareMoney(spentCanonical, '0') <= 0) return 0;
  if (compareMoney(spentCanonical, totalCanonical) >= 0) return 100;
  const spentDigits = toScaledDigits(spentCanonical);
  const totalDigits = toScaledDigits(totalCanonical);
  // floor(spent * 100 / total) via integer division on digit strings
  const scaled = `${spentDigits}00`.replace(/^0+(?=\d)/, '') || '0';
  let remainder = '';
  let quotient = '';
  for (const ch of scaled) {
    remainder = `${remainder}${ch}`.replace(/^0+(?=\d)/, '') || '0';
    let qDigit = 0;
    while (compareDigitStrings(remainder, totalDigits) >= 0) {
      const next = subtractDigitStrings(remainder, totalDigits);
      remainder = next.digits;
      qDigit += 1;
    }
    quotient += String(qDigit);
  }
  const percent = Number((quotient.replace(/^0+(?=\d)/, '') || '0'));
  if (!Number.isFinite(percent) || percent < 0) return 0;
  return percent > 100 ? 100 : percent;
}

/**
 * Equal-share preview (floor division). UX hint only — server remains authoritative.
 */
export function divideMoneyByInteger(amount, divisor) {
  const n = Number(divisor);
  if (!Number.isInteger(n) || n <= 0) return null;
  const canonical = moneyToCanonicalString(amount);
  if (!canonical) return null;
  const digits = toScaledDigits(canonical);
  let remainder = '';
  let quotient = '';
  for (const ch of digits) {
    remainder = `${remainder}${ch}`.replace(/^0+(?=\d)/, '') || '0';
    let qDigit = 0;
    const divisorDigits = String(n);
    while (compareDigitStrings(remainder, divisorDigits) >= 0) {
      const next = subtractDigitStrings(remainder, divisorDigits);
      remainder = next.digits;
      qDigit += 1;
    }
    quotient += String(qDigit);
  }
  return fromScaledDigits(quotient.replace(/^0+(?=\d)/, '') || '0');
}

/** True when canonical amount is strictly positive (backend @Positive). */
export function isPositiveMoney(value) {
  const canonical = moneyToCanonicalString(value);
  if (!canonical || canonical.startsWith('-')) return false;
  return compareMoney(canonical, '0') > 0;
}

/**
 * Canonical scale-8 string that may be signed (Split netAmount = paid - owed).
 * Absolute portion reuses unsigned money rules; leading '-' preserved.
 */
export function moneyToCanonicalStringSigned(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const negative = raw.startsWith('-');
  const absolute = moneyToCanonicalString(negative ? raw.slice(1) : raw);
  if (absolute == null) return null;
  if (!absolute) return '';
  if (absolute === '0.00000000') return '0.00000000';
  return negative ? `-${absolute}` : absolute;
}

export function formatSignedMoneyDisplay(value) {
  const canonical = moneyToCanonicalStringSigned(value);
  if (!canonical) return String(value ?? '');
  const negative = canonical.startsWith('-');
  const display = formatMoneyDisplay(negative ? canonical.slice(1) : canonical);
  return negative ? `-${display}` : display;
}

/** Compare signed scale-8 money strings. */
export function compareMoneySigned(left, right) {
  const a = moneyToCanonicalStringSigned(left);
  const b = moneyToCanonicalStringSigned(right);
  if (!a || !b) return 0;
  const aNeg = a.startsWith('-');
  const bNeg = b.startsWith('-');
  if (aNeg !== bNeg) return aNeg ? -1 : 1;
  const cmp = compareDigitStrings(
    toScaledDigits(aNeg ? a.slice(1) : a),
    toScaledDigits(bNeg ? b.slice(1) : b),
  );
  return aNeg ? -cmp : cmp;
}

/**
 * Split netAmount semantics (domain: net = paid - owed).
 * positive → 應收 (paid more); negative → 應付 (owed more); zero → 已平衡.
 */
export function netBalanceKind(netAmount) {
  const cmp = compareMoneySigned(netAmount, '0');
  if (cmp > 0) return 'receivable';
  if (cmp < 0) return 'payable';
  return 'settled';
}

export function netBalanceLabel(kind) {
  if (kind === 'receivable') return '應收';
  if (kind === 'payable') return '應付';
  return '已平衡';
}
